import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import { useTranslation, useAppDispatch, useOnClickOutside, useAppSelector, useGAEventCommonProps } from 'client/hooks';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import { selectUserName, selectIsLoggedIn, selectUserUID } from 'client/redux/client/selectors';
import { deleteMessage, getReactionsMessagesCount, publishMessage } from 'client/redux/pubnub/actions';
import { setListenerHistory } from 'client/redux/room/actions';
import { LS_KEYS, PB_CHANNELS } from 'client/redux/pubnub/constants';
import { selectRoomDbData, selectRoomId } from 'client/redux/room/selectors';
import { MessageType } from 'client/redux/pubnub/types';
import { EMOJI_TYPES } from 'client/types';
import { roomInfoLS, analytics } from 'client/utils';

import TransitionComponent from '../TransitionComponent';
import EmotionsMenu from './EmotionsMenu';
import Icon from '../Icon';

import css from './EmotionsActions.module.scss';

interface Props {
  className?: string;
  isAnonymous: boolean;
  showToLoginPopUp: (text: 'emotions' | 'speaking') => void;
}

const EmotionsActions: React.FC<Props> = (props) => {
  const { className = '', isAnonymous, showToLoginPopUp } = props;
  const { translate } = useTranslation('actionsPanel');
  const dispatch = useAppDispatch();
  const userName = useAppSelector(selectUserName);
  const userId = useAppSelector(selectUserUID);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const roomId = useAppSelector(selectRoomId);
  const roomData = useAppSelector(selectRoomDbData);
  const [isActiveEmotionsMenu, setIsActiveEmotionsMenu] = useState<boolean>(false);
  const [activeEmoji, setActiveEmoji] = useState<EMOJI_TYPES | ''>('');
  const emotionsWrapperRef = React.useRef<HTMLDivElement>(null);
  const { common } = useGAEventCommonProps();

  useEffect(() => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const activeEmoji = lsRoomInfo?.[LS_KEYS.LS_REACTION_MSG_VALUE];

    if (activeEmoji) setActiveEmoji(activeEmoji);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) setActiveEmoji('');
  }, [isLoggedIn]);

  const clickOnBtn = () => {
    if (isAnonymous) {
      showToLoginPopUp('emotions');
      dispatch(setIsShowFooterPopUpOverlay(true));
    } else {
      if (!isActiveEmotionsMenu) {
        analytics.gtag.event('audio_chat_content_click', {
          ...common,
          text_clicked: translate('btns.emojiesBtn'),
        });

        dispatch(getReactionsMessagesCount({ roomId }));
      }
      setIsActiveEmotionsMenu(!isActiveEmotionsMenu);
    }
  };

  const closeMenu = () => {
    setIsActiveEmotionsMenu(false);
  };

  const setEmoji = async (emoji: EMOJI_TYPES) => {
    // * if users's message already exist in the channel 'reactions' delete it first
    const lsRoomInfo = roomInfoLS.getData(roomId);
    const messageTT = lsRoomInfo?.[LS_KEYS.LS_REACTION_MSG_TT];

    if (emoji === activeEmoji) {
      if (activeEmoji && messageTT) {
        await dispatch(
          deleteMessage({
            channel: `${roomId}.${PB_CHANNELS.REACTIONS}.${activeEmoji}`,
            timeToken: messageTT,
            messageType: MessageType.REACTION,
          }),
        );

        dispatch(
          setListenerHistory({
            uid: userId,
            roomId: roomId,
            name: userName,
            reaction: null,
            sentReactionTime: null,
          }),
        );
      }

      setActiveEmoji('');
      roomInfoLS.setData(roomId, { [LS_KEYS.LS_REACTION_MSG_VALUE]: null });
      setIsActiveEmotionsMenu(false);
    } else {
      if (activeEmoji && messageTT) {
        await dispatch(
          deleteMessage({
            channel: `${roomId}.${PB_CHANNELS.REACTIONS}.${activeEmoji}`,
            timeToken: messageTT,
            messageType: MessageType.REACTION,
          }),
        );
      }

      if (roomData?.date) {
        const dateOfStart = new Date(roomData?.date);

        dispatch(
          setListenerHistory({
            uid: userId,
            roomId: roomId,
            name: userName,
            reaction: emoji,
            sentReactionTime: Date.now() - dateOfStart.valueOf(),
          }),
        );
      }

      dispatch(
        publishMessage({
          channel: `${roomId}.${PB_CHANNELS.REACTIONS}.${emoji}`,
          message: JSON.stringify({ type: MessageType.REACTION, id: userId, name: userName }),
          onPublish: (timetoken) => {
            roomInfoLS.setData(roomId, { [LS_KEYS.LS_REACTION_MSG_TT]: `${timetoken}` });
          },
        }),
      );

      setActiveEmoji(emoji);
      roomInfoLS.setData(roomId, { [LS_KEYS.LS_REACTION_MSG_VALUE]: emoji });
      setIsActiveEmotionsMenu(false);
    }
  };

  useOnClickOutside<HTMLDivElement>(emotionsWrapperRef, closeMenu);

  return (
    <div className={cn(css.emotionsActionsWrapper, className)} ref={emotionsWrapperRef}>
      <div className={css.btn} onClick={clickOnBtn} data-testid="emotions-btn">
        <div className={cn(css.iconWrapper, activeEmoji && css.isActiveEmoji)}>
          {activeEmoji ? (
            <Icon type={activeEmoji} className={css.icon} />
          ) : (
            <Icon type={isActiveEmotionsMenu ? 'reaction_red' : 'reaction'} className={css.icon} />
          )}
        </div>
        <div className={css.name}>{translate('btns.emojiesBtn')}</div>
      </div>
      <TransitionComponent type="opacity" visible={isActiveEmotionsMenu}>
        <EmotionsMenu closeMenu={closeMenu} setEmoji={setEmoji} />
      </TransitionComponent>
    </div>
  );
};

export default EmotionsActions;
